import axios from 'axios';
import { z } from 'zod';

import { strictQuery } from 'utils/strict-query';

import { AdvisorsChangesZod } from 'types/zod/advisor-changes';
import { AdvisorInviteCodeZod } from 'types/zod/advisor-invite-code';
import { AdvisorParticipantZod } from 'types/zod/advisor-participant';
import { AdvisorPropertiesZod } from 'types/zod/advisor-properties';
import { AdvisorsGroupZod } from 'types/zod/advisors-group';
import { ClientZod } from 'types/zod/client';
import { CreateGroupDataZod } from 'types/zod/create-group-data';
import { ParticipantZod } from 'types/zod/participant';
import { PricingOptionsZod } from 'types/zod/pricing-options';
import { SearchResultZod } from 'types/zod/search-result';
import { UpdateGroupDataZod } from 'types/zod/update-group-data';

const ParticipantIdZod = ParticipantZod.shape.participant_id;
type ParticipantId = z.infer<typeof ParticipantIdZod>;

const GroupIdZod = AdvisorsGroupZod.shape.groupId;

const AdvisorApi = {
    getClients: strictQuery({
        In: z.union([z.object({ participantId: ParticipantIdZod }), z.object({ groupId: GroupIdZod })]).optional(),
        Out: z.array(ClientZod),
        handler: (params) => axios.get('/advisors/clients', { params }),
    }),

    getAdvisorInviteCodes: strictQuery({
        In: ParticipantIdZod.optional(),
        Out: z.array(AdvisorInviteCodeZod),
        handler: (participantId) =>
            axios.get('/advisors/invite-codes', { params: participantId ? { participantId } : null }),
    }),

    removeClient: strictQuery({
        In: z.object({ participantId: ParticipantIdZod, advisorParticipantId: ParticipantIdZod }),
        Out: AdvisorsChangesZod,
        handler: ({ participantId, advisorParticipantId }) =>
            axios.patch(`/advisors/clients/${participantId}/unlink-from-advisor/${advisorParticipantId}`),
    }),

    getAdvisorByCode: strictQuery({
        In: z.string(),
        Out: AdvisorParticipantZod,
        handler: (code) => axios.get('/advisors', { params: { invite_code: code } }),
    }),

    linkAdvisor: strictQuery({
        In: z.string(),
        Out: AdvisorsChangesZod,
        handler: (invite) => axios.patch('/participants/link-advisor', null, { params: { invite } }),
    }),

    createZendeskTicket: strictQuery({
        In: z.string(),
        Out: z.any(),
        handler: (text) => axios.post('/advisors/feedback', { text }),
    }),

    getAllGroups: strictQuery({
        In: z.void(),
        Out: z.array(AdvisorsGroupZod),
        handler: () => axios.get('/advisors/groups'),
    }),

    getGroup: strictQuery({
        In: GroupIdZod,
        Out: AdvisorsGroupZod,
        handler: (groupId) => axios.get('/advisors/groups', { params: { group_id: groupId } }),
    }),

    createGroup: strictQuery({
        In: CreateGroupDataZod,
        Out: AdvisorsChangesZod,
        handler: (groupData) => axios.post('/advisors/groups', groupData),
    }),

    removeAdvisorFromGroup: strictQuery({
        In: z.object({ participantId: ParticipantIdZod, groupId: GroupIdZod }),
        Out: AdvisorsChangesZod,
        handler: ({ participantId, groupId }) => axios.patch(`/advisors/${participantId}/remove-from-group/${groupId}`),
    }),

    moveAdvisorToGroup: strictQuery({
        In: z.object({ participantId: ParticipantIdZod, parentGroupId: GroupIdZod }),
        Out: AdvisorsChangesZod,
        handler: ({ participantId, parentGroupId }) =>
            axios.patch(`/advisors/${participantId}/move-to-group/${parentGroupId}`),
    }),

    moveGroupToNewParentGroup: strictQuery({
        In: z.object({ groupId: GroupIdZod, parentGroupId: GroupIdZod }),
        Out: AdvisorsChangesZod,
        handler: ({ groupId, parentGroupId }) =>
            axios.patch(`/advisors/groups/${groupId}/move-to-group/${parentGroupId}`),
    }),

    editGroup: strictQuery({
        In: UpdateGroupDataZod,
        Out: AdvisorsChangesZod,
        handler: (groupData) => axios.patch(`/advisors/groups/${groupData.groupId}`, groupData),
    }),

    removeGroup: strictQuery({
        In: GroupIdZod,
        Out: AdvisorsChangesZod,
        handler: (groupId) => axios.delete(`/advisors/groups/${groupId}`),
    }),

    unlinkClientFromAdvisor: strictQuery({
        In: z.object({ clientParticipantId: ParticipantIdZod, oldAdvisorParticipantId: ParticipantIdZod }),
        Out: z.unknown(),
        handler: ({ clientParticipantId, oldAdvisorParticipantId }) =>
            axios.patch(`/advisors/clients/${clientParticipantId}/unlink-from-advisor/${oldAdvisorParticipantId}`),
    }),

    linkClientToAdvisor: strictQuery({
        In: z.object({ clientParticipantId: ParticipantIdZod, advisorParticipantId: ParticipantIdZod }),
        Out: AdvisorsChangesZod,
        handler: ({ clientParticipantId, advisorParticipantId }) =>
            axios.patch(`/advisors/clients/${clientParticipantId}/link-to-advisor/${advisorParticipantId}`),
    }),

    // TODO: Need to create one endpoint for changing advisor
    changeAdvisor: async (params: {
        clientParticipantId: ParticipantId;
        oldAdvisorParticipantId: ParticipantId;
        advisorParticipantId: ParticipantId;
    }) => {
        const { clientParticipantId, oldAdvisorParticipantId, advisorParticipantId } = params;
        await AdvisorApi.unlinkClientFromAdvisor({ clientParticipantId, oldAdvisorParticipantId });
        const data = await AdvisorApi.linkClientToAdvisor({ clientParticipantId, advisorParticipantId });
        return data;
    },

    linkAdvisorToGroup: strictQuery({
        In: z.string(),
        Out: AdvisorsChangesZod,
        handler: (invite) => axios.patch('/advisors/link-to-group', null, { params: { invite } }),
    }),

    sendInvite: strictQuery({
        In: z.object({ advisor_email: z.string(), new_group_id: GroupIdZod }),
        Out: z.any(),
        handler: ({ advisor_email, new_group_id }) =>
            axios.post('/advisors/groups/invite', { advisor_email, new_group_id }),
    }),

    getGroupByCode: strictQuery({
        In: z.string(),
        Out: AdvisorsGroupZod,
        handler: (invite) => axios.get('/advisors/groups', { params: { invite } }),
    }),

    handoverClient: strictQuery({
        In: ParticipantIdZod,
        Out: z.unknown(),
        handler: (id) => axios.patch(`/advisors/clients/${id}/handover`),
    }),

    search: strictQuery({
        In: z
            .object({
                query: z.string().optional(),
                type: z.union([z.literal('groups'), z.literal('advisors'), z.literal('clients')]).optional(),
                groupId: GroupIdZod.optional(),
                participantId: ParticipantIdZod.optional(),
            })
            .optional(),
        Out: SearchResultZod,
        handler: (params) => axios.get('/advisors/search', { params }),
    }),

    setGroupPricingOptions: strictQuery({
        In: z.object({ groupId: GroupIdZod, pricingOptions: PricingOptionsZod }),
        Out: PricingOptionsZod,
        handler: ({ groupId, pricingOptions }) =>
            axios.put(`/advisors/groups/${groupId}/pricing-options`, pricingOptions),
    }),

    setAdvisorPricingOptions: strictQuery({
        In: z.object({ participantId: ParticipantIdZod, pricingOptions: PricingOptionsZod }),
        Out: PricingOptionsZod,
        handler: ({ participantId, pricingOptions }) =>
            axios.put(`/advisors/${participantId}/pricing-options`, pricingOptions),
    }),

    getGroupPricingOptions: strictQuery({
        In: GroupIdZod,
        Out: PricingOptionsZod,
        handler: (groupId) => axios.get(`/advisors/groups/${groupId}/pricing-options`),
    }),

    getAdvisorPricingOptions: strictQuery({
        In: ParticipantIdZod,
        Out: PricingOptionsZod,
        handler: (participantId) => axios.get(`/advisors/${participantId}/pricing-options`),
    }),

    setAdvisorProperties: strictQuery({
        In: AdvisorPropertiesZod.and(z.object({ advisorParticipantId: ParticipantIdZod })),
        Out: z.object({ participants: z.array(AdvisorParticipantZod) }),
        handler: (properties) => axios.post(`/advisors/properties`, [properties]),
    }),
};

export default AdvisorApi;
